/**
 * Post Analyzer Component
 * Analyzes external posts and offers to create improved versions
 * FREE analysis for paid users, locked for trial
 * Uses POST CREDIT to create improved version (not regen credit)
 */

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, apiClient } from "@/lib/api";
import { 
  Link2, 
  Search, 
  Loader2, 
  AlertTriangle, 
  CheckCircle2, 
  TrendingUp,
  Sparkles,
  Lock,
  ArrowRight,
  ExternalLink,
  Image as ImageIcon,
  ChevronDown,
  ChevronUp,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

// Grade color helper
const getGradeColor = (grade) => {
  if (grade?.startsWith('A')) return 'text-green-400 bg-green-500/20';
  if (grade?.startsWith('B')) return 'text-blue-400 bg-blue-500/20';
  if (grade?.startsWith('C')) return 'text-yellow-400 bg-yellow-500/20';
  return 'text-red-400 bg-red-500/20';
};

// Score bar component
function ScoreBar({ name, score, issues }) {
  const [expanded, setExpanded] = useState(false);
  
  const getBarColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-blue-500';
    if (score >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="py-2">
      <div 
        className="flex justify-between items-center mb-1 cursor-pointer hover:bg-white/5 rounded px-1 -mx-1"
        onClick={() => issues?.length > 0 && setExpanded(!expanded)}
      >
        <span className="text-sm font-medium flex items-center gap-2">
          {name}
          {issues?.length > 0 && (
            <span className="text-xs text-viraly-muted">
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </span>
          )}
        </span>
        <span className="text-sm text-viraly-muted">{score}/100</span>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <motion.div
          className={`h-full ${getBarColor(score)} rounded-full`}
          initial={{ width: 0 }}
          animate={{ width: `${score}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
      </div>
      <AnimatePresence>
        {expanded && issues?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2 space-y-1"
          >
            {issues.map((issue, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-yellow-400/80">
                <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                <span>{issue}</span>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function PostAnalyzer() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [postUrl, setPostUrl] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  
  // Auto-detect platform from URL
  const detectPlatform = (url) => {
    if (!url) return "instagram";
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('instagram.com') || lowerUrl.includes('instagr.am')) return "instagram";
    if (lowerUrl.includes('facebook.com') || lowerUrl.includes('fb.com') || lowerUrl.includes('fb.watch')) return "facebook";
    if (lowerUrl.includes('tiktok.com')) return "tiktok";
    if (lowerUrl.includes('pinterest.com') || lowerUrl.includes('pin.it')) return "pinterest";
    if (lowerUrl.includes('linkedin.com')) return "linkedin";
    if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) return "youtube";
    if (lowerUrl.includes('twitter.com') || lowerUrl.includes('x.com')) return "twitter";
    return "instagram";
  };
  
  const detectedPlatform = detectPlatform(postUrl);
  const [analysis, setAnalysis] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createdPost, setCreatedPost] = useState(null);

  const isTrial = user?.plan === "free_trial";

  const handleAnalyze = async () => {
    if (!postUrl.trim()) {
      toast.error("Please enter a post URL");
      return;
    }

    setAnalyzing(true);
    setAnalysis(null);
    setCreatedPost(null);

    try {
      const response = await apiClient.post('/content/analyze-external-post', {
        post_url: postUrl,
        platform
      });
      setAnalysis(response.data);
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to analyze post";
      toast.error(msg);
      
      // If trial user, show upgrade prompt
      if (error.response?.status === 403) {
        setAnalysis({ locked: true });
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const handleCreateImproved = async () => {
    setCreating(true);
    
    try {
      const response = await apiClient.post('/content/create-improved-post', {
        post_url: postUrl,
        platform: detectedPlatform,
        output_type: "post"
      });
      
      setCreatedPost(response.data);
      toast.success("Improved post created! Check your Posts tab.");
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to create improved post";
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="post-analyzer">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="font-heading text-2xl font-bold mb-2">Post Analyzer</h2>
        <p className="text-viraly-muted">
          Paste a link to any of your existing posts. We'll analyze what's working and what's not.
        </p>
      </div>

      {/* Input Section */}
      <div className="card-base p-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* URL Input */}
          <div className="flex-1">
            <label className="block text-sm font-medium mb-2">Post URL</label>
            <div className="relative">
              <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-viraly-muted" />
              <input
                type="url"
                value={postUrl}
                onChange={(e) => setPostUrl(e.target.value)}
                placeholder="https://instagram.com/p/... or any social post URL"
                className="input-field pl-11 w-full"
                data-testid="post-url-input"
              />
            </div>
            {postUrl && (
              <p className="text-xs text-viraly-muted mt-1">
                Platform detected: <span className="text-viraly-orange capitalize">{detectedPlatform}</span>
              </p>
            )}
          </div>

          {/* Analyze Button */}
          <div className="flex items-end">
            <Button
              onClick={handleAnalyze}
              disabled={analyzing || !postUrl.trim()}
              className="btn-primary w-full md:w-auto"
              data-testid="analyze-btn"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Analyze Post
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Trial User Lock Message */}
        {isTrial && (
          <div className="mt-4 p-4 rounded-xl bg-viraly-orange/10 border border-viraly-orange/30 flex items-start gap-3">
            <Lock className="w-5 h-5 text-viraly-orange flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium">Upgrade to unlock Post Analyzer</p>
              <p className="text-xs text-viraly-muted mt-1">
                Paid plans include free analysis of your existing posts plus AI-powered improvements.
              </p>
              <Button 
                onClick={() => navigate('/pricing')}
                className="mt-3 btn-primary text-sm"
                size="sm"
              >
                View Plans
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Analysis Results */}
      <AnimatePresence>
        {analysis && !analysis.locked && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="card-base p-6"
            data-testid="analysis-results"
          >
            {/* Score Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-heading text-xl font-bold">Analysis Results</h3>
                <p className="text-sm text-viraly-muted capitalize">{platform} post</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-3xl font-bold">{analysis.analysis?.overall_score}</p>
                  <p className="text-xs text-viraly-muted">out of 100</p>
                </div>
                <div className={`px-3 py-2 rounded-xl font-bold text-lg ${getGradeColor(analysis.analysis?.grade)}`}>
                  {analysis.analysis?.grade}
                </div>
              </div>
            </div>

            {/* Verdict */}
            <div className="mb-6 p-4 rounded-xl bg-white/5">
              <p className="text-sm">{analysis.analysis?.verdict}</p>
            </div>

            {/* Extracted Content Preview */}
            {analysis.extracted_content && (
              <div className="mb-6 p-4 rounded-xl bg-white/5 border border-white/10">
                <p className="text-xs text-viraly-muted mb-2">Original Content Detected:</p>
                <p className="text-sm line-clamp-3">{analysis.extracted_content.caption}</p>
                {analysis.extracted_content.image_url && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-viraly-muted">
                    <ImageIcon className="w-4 h-4" />
                    <span>Image detected</span>
                  </div>
                )}
              </div>
            )}

            {/* Factor Breakdown */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold mb-4">Score Breakdown</h4>
              {analysis.analysis?.factors?.map((factor, i) => (
                <ScoreBar 
                  key={i}
                  name={factor.name}
                  score={factor.score}
                  issues={factor.issues}
                />
              ))}
            </div>

            {/* Improvements Needed */}
            {analysis.improvements_needed?.length > 0 && (
              <div className="mb-6 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                <h4 className="text-sm font-semibold text-yellow-400 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  What's Missing
                </h4>
                <ul className="space-y-2">
                  {analysis.improvements_needed.map((issue, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-viraly-muted">
                      <span className="text-yellow-400">•</span>
                      {issue}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* CTA - Create Improved Version */}
            {!createdPost && (
              <div className="p-4 rounded-xl bg-gradient-to-r from-viraly-orange/20 to-yellow-500/20 border border-viraly-orange/30">
                <div className="flex flex-col gap-4">
                  <div>
                    <h4 className="font-semibold flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-viraly-orange" />
                      Create an improved version?
                    </h4>
                    <p className="text-sm text-viraly-muted mt-1">
                      We'll fix all the issues above and create an optimized post for you.
                    </p>
                    <p className="text-xs text-viraly-orange mt-2">
                      Uses 1 post credit • You have {user?.posts_remaining || 0} remaining
                    </p>
                  </div>
                  
                  {/* Show buy credits prompt if user has no credits */}
                  {(user?.posts_remaining || 0) <= 0 && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 mb-3">
                      <p className="text-sm text-red-400 font-medium flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        You're out of post credits!
                      </p>
                      <button
                        onClick={() => navigate("/pricing?tab=payg&item=posts")}
                        className="mt-2 text-sm text-viraly-orange hover:underline"
                      >
                        Buy more credits →
                      </button>
                    </div>
                  )}
                  
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={handleCreateImproved}
                      disabled={creating || (user?.posts_remaining || 0) <= 0}
                      className="btn-primary whitespace-nowrap flex-1"
                      data-testid="create-improved-btn"
                    >
                      {creating ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <TrendingUp className="w-4 h-4 mr-2" />
                          Create Improved Post
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => {
                        // Navigate to Create page and show input type selection
                        // Pass analyzed content as context so AI can use it
                        const originalCaption = analysis?.original_caption || '';
                        const improvements = analysis?.improvements?.join('. ') || '';
                        navigate('/create', { 
                          state: { 
                            fromPostAnalyzer: true,
                            analyzedContent: {
                              caption: originalCaption,
                              improvements: improvements,
                              platform: detectedPlatform,
                              suggestions: analysis?.improvements || []
                            },
                            showInputSelection: true // Force showing input type selection
                          } 
                        });
                      }}
                      variant="outline"
                      className="border-viraly-orange/50 text-viraly-orange hover:bg-viraly-orange/10 whitespace-nowrap flex-1"
                      data-testid="customize-improved-btn"
                    >
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Create My Own Version
                    </Button>
                  </div>
                  <p className="text-xs text-viraly-muted">
                    Want to add images, change templates, or customize further? Use "Customize in Create+"
                  </p>
                </div>
              </div>
            )}

            {/* Created Post Result */}
            {createdPost && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 rounded-xl bg-green-500/10 border border-green-500/30"
                data-testid="created-post-result"
              >
                <div className="flex items-start gap-3 mb-4">
                  <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-green-400">Improved Post Created!</h4>
                    <p className="text-sm text-viraly-muted">1 credit used. Find it in your Posts tab as a draft.</p>
                  </div>
                </div>
                
                {/* Side by Side Comparison */}
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-xs text-viraly-muted mb-2">Original:</p>
                    <p className="text-sm">{createdPost.original_caption}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-viraly-orange/10 border border-viraly-orange/20">
                    <p className="text-xs text-viraly-orange mb-2">Improved:</p>
                    <p className="text-sm">{createdPost.improved_caption}</p>
                  </div>
                </div>

                <div className="flex gap-3 mt-4">
                  <Button
                    onClick={() => navigate('/posts?tab=drafts')}
                    className="btn-primary"
                  >
                    View in Posts
                  </Button>
                  <Button
                    onClick={() => {
                      setCreatedPost(null);
                      setAnalysis(null);
                      setPostUrl("");
                    }}
                    variant="outline"
                    className="border-white/20"
                  >
                    Analyze Another
                  </Button>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Locked State for Trial Users */}
      {analysis?.locked && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-base p-8 text-center"
        >
          <Lock className="w-12 h-12 text-viraly-orange mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold mb-2">Upgrade to Analyze Posts</h3>
          <p className="text-viraly-muted mb-6">
            Post Analyzer is available on all paid plans. Upgrade to analyze your existing posts
            and let AI create improved versions.
          </p>
          <Button onClick={() => navigate('/pricing')} className="btn-primary">
            View Pricing Plans
          </Button>
        </motion.div>
      )}
    </div>
  );
}
